import React from 'react'
import Animated, {
  interpolate,
  useAnimatedStyle,
} from 'react-native-reanimated'
import { View, Pressable, Platform } from 'react-native'
import { useTranslation } from 'react-i18next'
import { router } from 'expo-router'

import { StepOption } from '@order/steps/types'
import { colors } from '@ui/colors'
import Typography from '@ui/typography'
import AttributeColor from '@order/steps/attributes/Color/Color'
import BlurEffect from '@ui/effects/BlurEffect'
import Gradient from '@ui/effects/Gradient'
import { hex } from '@ui/utils/hex'
import useAnimatedPress from '@ui/animated/useAnimatedPress'
import useRWD from '@ui/theme/useRWD'
import { ColorConfig } from '@ca/common/types'
import Border from '@ui/effects/Border'

interface Props {
  option: StepOption
  color: ColorConfig
}

const BLUR = 50

export default function OptionColor({ option, color }: Props) {
  const { t } = useTranslation(['steps'])
  const { animation, onPressIn, onPressOut } = useAnimatedPress()

  const styleGradient = useAnimatedStyle(() => ({
    opacity: interpolate(animation.value, [0, 1], [1, 0.75]),
  }))

  const styleMain = useAnimatedStyle(() => ({
    transform: [{ scale: interpolate(animation.value, [0, 1], [1, 1.1]) }],
  }))

  const { tiles } = useRWD()
  const width = tiles.color.width
  const height = tiles.color.height

  return (
    <Pressable
      onPress={option.onPress}
      onPressIn={onPressIn}
      onPressOut={onPressOut}
    >
      <View
        className="relative flex items-center justify-center overflow-hidden rounded-2xl"
        style={{
          width,
          height,
          backgroundColor: colors.gray[300],
        }}
      >
        <View className="absolute h-full w-full">
          <AttributeColor
            colorConfig={color}
            size={height}
            style={{
              position: 'absolute',
              right: -10,
              transform: [{ scaleX: 2 }],
            }}
          />
          {Platform.select({
            default: (
              <BlurEffect
                tint="regular"
                intensity={BLUR}
                className="left-0 h-full"
              />
            ),
            web: (
              <div
                className="absolute left-0 top-0 h-full w-full"
                style={{ backdropFilter: `blur(${BLUR}px)` }}
              />
            ),
          })}
          <Gradient
            colors={[
              hex(colors.gray[75], 1),
              hex(colors.gray[75], 1),
              colors.white0,
            ]}
            start={[0, 1]}
            end={[1, 0]}
            style={styleGradient}
          />
          <Border />
        </View>
        <View className="flex w-full flex-row items-center justify-between px-12">
          <View className="w-2/3 gap-1">
            <Typography
              variant={Platform.select({ default: 'MDB', web: 'MDB' })}
              className="whitespace-break-spaces leading-tight"
            >
              {t(option.title)}
            </Typography>
            {/* {option.desc && (
              <Typography
                variant={Platform.select({ default: 'SM', web: 'MD' })}
                className="color-desc leading-tight"
              >
                {option.desc}
              </Typography>
            )} */}
          </View>
          <Animated.View style={styleMain}>
            <AttributeColor colorConfig={color} size={64} />
          </Animated.View>
        </View>
      </View>
    </Pressable>
  )
}
